import {
  GridActionsCellItem,
  GridColDef,
  GridPaginationModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import { AppDataGrid } from "../../../../components/datagrid/AppDataGrid";
import { useEffect, useMemo, useState } from "react";
import { tableStyle } from "./GoalSettingTableStyle";
import { Box, Checkbox, Chip, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { common, input } from "../../../../i18n";
import { QuickSearchToolbar } from "./QuickSearchToolbar";
import { GoalSettingContext } from "../../../../contexts/GoalSettingContext";
import alertService from "../../../../components/alert/alertService";
import { goalSettingService } from "../../../../service/goal-setting.service";
import { ApiError } from "../../../../api/ApiError";
import { AddGoalSettingLayout } from "../../../goal-setting-form/AddGoalSettingLayout";
import { useSearchParams } from "react-router-dom";
import {
  isAddGoalDisabled,
  isGoalCapRefusal,
} from "../../../../contexts/useGoalLimit";
import dayjs from "dayjs";

export const GOAL_STATUS = [
  {
    status: "Complete",
    textColor: "var(--green)",
    bgColcor: "var(--status-approved-complete-active-bg)",
  },
  {
    status: "In Progress",
    textColor: "var(--status-pending-text)",
    bgColcor: "var(--status-pending-bg)",
  },
];

type Props = {};

const GoalManagementDataGrid = (props: Props) => {
  const {
    modalIsOpen,
    selectionModel,
    setSelectionModel,
    rows,
    setRows,
    goals,
    setGoals,

    setGoalInfo,
    setIsEditMode,
    setMethodology,
    setModalIsOpen,
    goalLock,
    isGoalListLoaded,
    getGoalSettingList,
  } = GoalSettingContext();
  const addGoalDisabled = isAddGoalDisabled(goalLock);

  const { t, i18n } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const openFromUrl = useMemo(
    () => searchParams.get("open") === "true",
    [searchParams],
  );
  // ?open=true (the dashboard's Add Goal) waits for the plan and the goal count, then leaves the URL
  useEffect(() => {
    if (!openFromUrl || goalLock.state === "loading" || !isGoalListLoaded) {
      return;
    }
    if (!addGoalDisabled) {
      setModalIsOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [
    openFromUrl,
    goalLock.state,
    isGoalListLoaded,
    addGoalDisabled,
    searchParams,
    setSearchParams,
    setModalIsOpen,
  ]);

  const handleDuplicateGoal = async (
    goalRefId: string,
    currentName: string,
  ) => {
    const isConfirmed = await alertService.confirm(
      t("Duplicate-Goal"),
      t("Are-you-sure-to-duplicate-this-goal", {
        param: currentName,
      }),
      t("Duplicate"),
    );

    if (!isConfirmed) return;

    const existingCopies = goals
      .filter((g) => g.name.startsWith(`${currentName}-copy`))
      .map((g) => g.name);

    let newName = `${currentName}-copy`;

    if (existingCopies.includes(newName)) {
      let suffix = 1;
      while (existingCopies.includes(`${currentName}-copy${suffix}`)) {
        suffix++;
      }
      newName = `${currentName}-copy${suffix}`;
    }

    let response;
    try {
      response = await goalSettingService.duplicateGoal(goalRefId, newName);
    } catch (error) {
      // error alert already shown by service; a cap refusal means the list is stale, so reload it to show the lock
      if (isGoalCapRefusal(error)) await getGoalSettingList();
      return;
    }

    if (response?.goals) {
      setGoals(response.goals);
      alertService.mixin(t("Duplicate-Goal-Successful"));
    }
  };

  const handleDeleteThisGoal = async (
    goalRefId: string,
    currentName: string,
  ) => {
    const isConfirmed = await alertService.confirm(
      t("Delete-Goal"),
      <Trans
        t={t}
        i18nKey="Confirm-Delete-Multiple-Goals"
        values={{ names: currentName }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
      t("Confirm"),
      "var(--red)",
    );
    if (!isConfirmed) return;
    if (isConfirmed) {
      let result;
      try {
        result = await goalSettingService.deleteGoals([goalRefId]);
      } catch {
        // error alert already shown by service
        return;
      }
      if (result) {
        setGoals(result.goals);
        alertService.mixin(t("Delete-Goal-Successful"));
      }
    }
  };

  const apiRef = useGridApiRef();

  const columns: GridColDef[] = [
    {
      field: "select",
      headerAlign: "left",
      width: 130,
      renderHeader: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            id={`checkbox-all`}
            checked={checkedAll()}
            indeterminate={indeterminateAll()}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleCheckboxAll()}
          />
          <Typography variant="body1">{t("ID")}</Typography>
        </Box>
      ),
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            id={`checkbox-${params.row._id}`}
            checked={selectionModel.includes(params.row.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              handleCheckboxChange(params.row.id);
            }}
          />
          <Typography variant="body1">{`GS-${params.row.id}`}</Typography>
        </Box>
      ),
    },
    {
      field: "updatedAt",
      headerName: t("Last-Update"),
      minWidth: 110,
      disableColumnMenu: true,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "",
    },
    {
      field: "name",
      headerName: t("Goal-Name"),
      minWidth: 120,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "left",
      flex: 1,
    },
    {
      field: "methodology",
      headerName: t("Methodology"),
      width: 120,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "left",
      renderCell: (params) => {
        if (params.value === "baseYear") return t("Base-Year");
        if (params.value === "baseline") return t("Base-Line");
      },
    },
    {
      field: "standard",
      headerName: t("Standard"),
      width: 120,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "center",
      renderCell: (params) =>
        params.value === "ghgProtocol" ? "GHG Protocol" : params.value,
    },
    {
      field: "scope",
      headerName: t("Scope"),
      width: 88,
      disableColumnMenu: true,
      sortable: false,
      type: "string",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        if (params.value === "scope12") return "1-2";
        if (params.value === "scope123") return "1-3";
        return params.value;
      },
    },
    {
      field: "startYear",
      headerName: t("Starting-Year"),
      width: 110,
      disableColumnMenu: true,
      sortable: false,
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "targetYear",
      headerName: t("Target-Year"),
      width: 100,
      disableColumnMenu: true,
      sortable: false,
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "goalReduction",
      headerName: t("Goal-Reduction"),
      width: 100,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${Number(params.value).toFixed(2)}%`,
    },
    {
      field: "toDateReduction",
      headerName: t("To-Date-Reduction"),
      width: 100,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => `${Number(params.value).toFixed(2)}%`,
    },
    {
      field: "status",
      headerName: t("Status"),
      width: 120,
      disableColumnMenu: true,
      type: "string",
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const item = GOAL_STATUS.find((e) => e.status === params.row.status);
        return (
          <Chip
            size="small"
            label={
              item?.status === "In Progress"
                ? t("In-Progress")
                : t(params.row.status)
            }
            sx={{
              backgroundColor: `${item?.bgColcor ?? null}`,
              color: `${item?.textColor ?? null}`,
              fontSize: "12px",
            }}
          />
        );
      },
    },
    {
      field: "", // Actions Column
      headerName: "",
      disableColumnMenu: true,
      sortable: false,
      type: "actions",
      width: 85,
      getApplyQuickFilterFn: undefined,
      getActions: (params) => [
        <GridActionsCellItem
          key={`${params.row.id}-copy`}
          disabled={addGoalDisabled}
          icon={<img src="/img/document-copy.svg" alt="icon-duplicate" />}
          label="duplicate"
          onClick={(e) => {
            e.stopPropagation();
            handleDuplicateGoal(params.row.goalRefId, params.row.name);
          }}
        />,
        <GridActionsCellItem
          key={`${params.row.id}-trash`}
          icon={<img src="/img/trash.svg" alt="icon-trash" />}
          label="delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteThisGoal(params.row.goalRefId, params.row.name);
          }}
        />,
      ],
    },
  ];

  useEffect(() => {
    const sorted = [...goals].sort((a, b) => b.id - a.id);
    setRows(sorted);
  }, [goals]);

  const checkedAll = () => {
    if (selectionModel.length === 0) return false;
    const currentDisplayItem = getCurrentDisplayItem();
    return currentDisplayItem.every((e) => selectionModel.includes(e));
  };

  const indeterminateAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();
    return (
      currentDisplayItem.some((e) => selectionModel.includes(e)) &&
      !checkedAll()
    );
  };

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 12,
    page: 0,
  });

  const getCurrentDisplayItem = () => {
    const firstIndex = paginationModel.page * paginationModel.pageSize;
    const lastIndex = (paginationModel.page + 1) * paginationModel.pageSize;
    const currentDisplayItem = [
      ...Array.from(apiRef.current.getRowModels().keys()).slice(
        firstIndex,
        lastIndex,
      ),
    ];
    return currentDisplayItem;
  };

  const handleCheckboxAll = () => {
    const currentDisplayItem = getCurrentDisplayItem();

    if (indeterminateAll()) {
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
      ]);
    }

    if (currentDisplayItem.every((e) => selectionModel.includes(e))) {
      return setSelectionModel(
        [...selectionModel, ...currentDisplayItem].filter(
          (e) => !currentDisplayItem.includes(e),
        ),
      );
    } else {
      return setSelectionModel([
        ...selectionModel.filter((e) => !currentDisplayItem.includes(e)),
        ...currentDisplayItem,
      ]);
    }
  };

  const handleCheckboxChange = (id: number) => {
    if (selectionModel.includes(id)) {
      setSelectionModel([...selectionModel, id].filter((e) => e !== id));
    } else {
      setSelectionModel([...selectionModel, id]);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width={"100%"}
      height={"100%"}
      flex={1}
    >
      {modalIsOpen && <AddGoalSettingLayout />}

      {modalIsOpen === false && (
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          width="100%"
          position="relative"
          minHeight={{ xs: "75vh", md: 400 }} // กำหนดความสูงขั้นต่ำกันตารางหดหาย
        >
          {/* ✅ 3. ขึง Absolute 4 มุม เพื่อบังคับให้ DataGrid ยืดสุดขอบกล่องเสมอ */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <AppDataGrid
              apiRef={apiRef}
              columns={columns}
              rows={rows}
              sx={{
                ...tableStyle(),
              }}
              rowHeight={48}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[12, 25, 50, 100]}
              onCellClick={async (param) => {
                try {
                  if (param.field === "select" || param.field === "") {
                    return;
                  }

                  const { goalRefId } = param.row;
                  if (!goalRefId) return;

                  alertService.showLoading();
                  const result = await goalSettingService.getGoalSettingInfo(
                    goalRefId,
                  );
                  alertService.closeLoading();

                  if (result) {
                    setGoalInfo(result);
                    setIsEditMode(true);
                    setMethodology(result.methodology);
                    setModalIsOpen(true);
                  }
                } catch (err) {
                  alertService.closeLoading();
                  if (err instanceof ApiError) return;
                  console.error("Error fetching goal info:", err);
                  await alertService.error(t("Error-Fetching-Goal-Data"));
                }
              }}
              slots={{
                toolbar: QuickSearchToolbar,
              }}
              localeText={{
                toolbarQuickFilterPlaceholder: `${t("search")}...`,
                noRowsLabel: `${t("No-Data")}`,
              }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: {
                    variant: "outlined",
                  },
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GoalManagementDataGrid;
